import { BaseController } from '../../../../base_controller';
import zoomSdk, { Apis } from '@zoom/appssdk';
import { MESSAGES } from '../integration_component/integration_component_controller';

export default class extends BaseController {
  static targets = [
    'inviteButton',
    'errorMessage',
    'startVoteButton',
    'closeWindowButton',
    'participantsMessage',
  ];

  declare inviteButtonTarget: HTMLButtonElement;
  declare startVoteButtonTarget: HTMLButtonElement;
  declare closeWindowButtonTarget: HTMLButtonElement;
  declare errorMessageTarget: HTMLElement;
  declare participantsMessageTarget: HTMLElement;

  static values = {
    userHost: Boolean,
    inMeeting: Boolean,
    macWorkaroundNeeded: Boolean,
    votersCount: Number,
  };

  declare userHostValue: boolean;
  declare inMeetingValue: boolean;
  declare macWorkaroundNeededValue: boolean;
  declare votersCountValue: number;

  async connect(): Promise<void> {
    this.runMacWorkaroundIfNeeded();
    await this.initializeZoom();
    await this.updateMeetingParticipants();
    this.setupListeners();
  }

  public async inviteParticipants(): Promise<void> {
    this.hideErrorMessage();
    this.disableInviteButton();

    try {
      await zoomSdk.sendAppInvitationToAllParticipants();
    } catch (e) {
      console.error(e);
      this.showErrorMessage('Something went wrong when inviting participants');
    } finally {
      this.enableInviteButton();
    }
  }

  public async sendFocusAppMessage(): Promise<void> {
    this.hideErrorMessage();
    this.disableStartVoteButton();

    try {
      const payload = { id: MESSAGES.FOCUS_APP };
      await zoomSdk.sendMessage({ payload });
      this.logger(`New message sent:`, payload);
    } catch (e) {
      console.error(e);
      this.showErrorMessage(
        'The configuration of the Zoom SDK encountered an error.'
      );
    } finally {
      setTimeout(() => {
        this.enableStartVoteButton();
      }, 1500);
    }
  }

  public async sendUnfocusAppMessage(): Promise<void> {
    this.hideErrorMessage();
    this.disableCloseWindowButton();

    try {
      const payload = { id: MESSAGES.UNFOCUS_APP };
      await zoomSdk.sendMessage({ payload });
      this.logger(`New message sent:`, payload);
    } catch (e) {
      console.error(e);
      this.showErrorMessage('There was a problem to send unfocus message');
    } finally {
      setTimeout(() => {
        this.enableCloseWindowButton();
      }, 1500);
    }
  }

  // private methods

  private runMacWorkaroundIfNeeded(): void {
    if (this.macWorkaroundNeededValue) {
      // Set a flag in localStorage to prevent infinite refresh loops
      const hasAttemptedRefresh = localStorage.getItem('zoomContextRefreshAttempted');

      if (!hasAttemptedRefresh) {
        localStorage.setItem('zoomContextRefreshAttempted', 'true');

        // Wait a brief moment to ensure any initial loading is complete
        setTimeout(() => {
          // Perform the refresh; hopefully the context will be available after
          window.location.reload();
        }, 500);
      } else {
        // We've already tried refreshing but still don't have context
        // Clear the flag for next session
        localStorage.removeItem('zoomContextRefreshAttempted');

        console.error("Zoom context still missing after refresh attempt");
        this.showErrorMessage('There was an error connecting to Zoom. Please restart the application.');
      }
    } else {
      if (localStorage.getItem('zoomContextRefreshAttempted')) {
        localStorage.removeItem('zoomContextRefreshAttempted');
      }
    }
  }

  private async initializeZoom(): Promise<void> {
    try {
      const configResponse = await zoomSdk.config({
        capabilities: this.hostCapabilities(),
      });

      this.logger('Zoom JS SDK Configuration', configResponse);
    } catch (e) {
      console.error(e);
      this.showErrorMessage('There was an error configuring the Zoom SDK');
    }
  }

  private hostCapabilities(): Apis[] {
    return [
      'shareApp',
      'sendAppInvitationToAllParticipants',
      'bringAppToFront',
      'sendMessage',
      'onMessage',
      'expandApp',
      'sendAppToBackground',
      'onSendAppInvitation',
      'onParticipantChange',
      'getMeetingParticipants',
    ];
  }

  private async getParticipantsCount() {
    const { participants } = await zoomSdk.getMeetingParticipants();

    return participants.length - 1;
  }

  private async updateMeetingParticipants() {
    if (!this.inMeetingValue) return;

    const participantsCount = await this.getParticipantsCount();
    const message = `Meeting Participants: ${participantsCount}/${this.votersCountValue}`;
    this.participantsMessageTarget.textContent = message;
  }

  private setupListeners(): void {
    zoomSdk.onSendAppInvitation((event) => {
      this.logger(`@onSendAppInvitation:`, event);
    });

    zoomSdk.onParticipantChange((event) => {
      this.logger(`@onParticipantChange:`, event);

      this.updateMeetingParticipants();
    });
  }

  protected logger(message, details): void {
    const finalMessage = `[ZoomIntegration] ${message}`;

    super.logger(finalMessage, details, '#b526b0');
  }

  private enableInviteButton(): void {
    this.inviteButtonTarget.disabled = false;
  }

  private disableInviteButton(): void {
    this.inviteButtonTarget.disabled = true;
  }

  private enableStartVoteButton(): void {
    this.startVoteButtonTarget.disabled = false;
  }

  private disableStartVoteButton(): void {
    this.startVoteButtonTarget.disabled = true;
  }

  private enableCloseWindowButton(): void {
    this.closeWindowButtonTarget.disabled = false;
  }

  private disableCloseWindowButton(): void {
    this.closeWindowButtonTarget.disabled = true;
  }

  private showErrorMessage(message) {
    this.errorMessageTarget.classList.remove('hide');
    this.errorMessageTarget.textContent = message;
  }

  private hideErrorMessage() {
    this.errorMessageTarget.classList.add('hide');
  }
}
